/**
 * Markdown for task prose — a small parser and the component that renders it.
 *
 * Markdown rather than stored HTML, and a parser rather than a library, for one reason each.
 *
 * The point of a task in this module is that it can be pasted into Claude Code, and
 * `taskAsPrompt` already emits a markdown document with the description inside it. Rich text
 * held as HTML would arrive in that prompt as tags, so the thing that makes the field readable
 * on screen would make it worse where it is actually used. Markdown is the one format that is
 * both the rendered field and the prompt.
 *
 * The parser is ~150 lines because the alternative is a dependency for a field on one page.
 * It renders to React elements, never to an HTML string, so there is no `dangerouslySetInnerHTML`
 * anywhere and no sanitiser to keep correct — an unrecognised construct comes out as the literal
 * text somebody typed, which is the failure mode you want.
 *
 * Deliberately not supported: tables, footnotes, reference links, raw HTML. Nothing that has
 * been wanted in a bug report.
 */

// Inline runs, in precedence order. Code first: a backtick span suppresses everything inside it.
// No lookbehind anywhere — Safari only gained it in 16.4, and a regex literal that fails to
// parse takes the whole bundle with it rather than degrading. The one rule that needs it
// (`snake_case` must not read as emphasis) is enforced in JS below instead.
// A source string rather than one shared RegExp: emphasis nests, `inlineNodes` recurses to
// render what is inside it, and a /g regex carries `lastIndex` on the object — so a shared one
// would have the inner call rewind the outer scan and the two would read the same text forever.
const INLINE_SOURCE =
  '(?<fence>`+)(?<code>[^]+?)\\k<fence>'
  + '|\\*\\*(?<strong>[^]+?)\\*\\*'
  + '|__(?<strong2>[^]+?)__'
  + '|\\*(?<em>[^*\\n]+?)\\*'
  + '|_(?<em2>[^_\\n]+?)_'
  + '|~~(?<del>[^]+?)~~'
  + '|\\[(?<linkText>[^\\]]*)\\]\\((?<linkHref>[^)\\s]+)\\)'
  + '|<(?<auto>[a-z][a-z0-9+.-]*:[^>\\s]+)>'
  + '|(?<bare>https?://[^\\s<>()\\[\\]"]+)';

// Anything not on this list is rendered as plain text rather than as a link. `javascript:` is
// the reason, but the rule is a list of what is allowed, not a list of what is blocked.
const SAFE_HREF = /^(https?:\/\/|mailto:)/i;

function isWord(ch) {
  return !!ch && /[\w]/.test(ch);
}

/** Inline markdown → React nodes. Recurses for the emphasis marks, which can nest. */
function inlineNodes(text, keyBase = 'x') {
  const inline = new RegExp(INLINE_SOURCE, 'g');
  const out = [];
  let last = 0;
  let n = 0;

  let m = inline.exec(text);
  while (m) {
    const g = m.groups;
    const key = `${keyBase}-${n++}`;

    // `files_norm` is one identifier, not an italic `norm`. A match whose underscores are both
    // touching word characters is skipped: `last` is left where it was, so the text falls through
    // into the next plain chunk verbatim.
    const intraword = g.em2 !== undefined
      && (isWord(text[m.index - 1]) || isWord(text[m.index + m[0].length]));

    if (!intraword) {
      if (m.index > last) out.push(text.slice(last, m.index));

      if (g.code !== undefined) {
        out.push(<code key={key}>{g.code}</code>);
      } else if (g.strong !== undefined || g.strong2 !== undefined) {
        out.push(<strong key={key}>{inlineNodes(g.strong ?? g.strong2, key)}</strong>);
      } else if (g.em !== undefined || g.em2 !== undefined) {
        out.push(<em key={key}>{inlineNodes(g.em ?? g.em2, key)}</em>);
      } else if (g.del !== undefined) {
        out.push(<s key={key}>{inlineNodes(g.del, key)}</s>);
      } else if (g.linkHref !== undefined) {
        out.push(SAFE_HREF.test(g.linkHref)
          ? <a key={key} href={g.linkHref} target="_blank" rel="noopener noreferrer">
              {inlineNodes(g.linkText || g.linkHref, key)}
            </a>
          : m[0]);
      } else {
        const url = g.auto ?? g.bare;
        out.push(SAFE_HREF.test(url)
          ? <a key={key} href={url} target="_blank" rel="noopener noreferrer">{url}</a>
          : m[0]);
      }

      last = m.index + m[0].length;
    }

    m = inline.exec(text);
  }

  if (last < text.length) out.push(text.slice(last));
  return out;
}

const RE = {
  fence: /^```\s*([\w+.-]*)\s*$/,
  heading: /^(#{1,4})\s+(.*)$/,
  rule: /^\s{0,3}([-*_])\s*(?:\1\s*){2,}$/,
  quote: /^\s{0,3}>\s?(.*)$/,
  bullet: /^(\s*)[-*+]\s+(.*)$/,
  number: /^(\s*)\d+[.)]\s+(.*)$/,
  task: /^\[([ xX])\]\s+(.*)$/,
};

function isBlockStart(line) {
  return RE.fence.test(line) || RE.heading.test(line) || RE.rule.test(line)
    || RE.quote.test(line) || RE.bullet.test(line) || RE.number.test(line);
}

/**
 * Flat list items → a nested tree, by indent.
 *
 * Indent is compared rather than counted in twos, because people indent a sub-item with two
 * spaces, four spaces or a tab and mean the same thing by all three.
 */
function nestItems(items, start, indent, out) {
  let i = start;
  while (i < items.length && items[i].indent >= indent) {
    if (items[i].indent > indent) {
      const child = { ordered: items[i].ordered, items: [] };
      i = nestItems(items, i, items[i].indent, child.items);
      const parent = out[out.length - 1];
      if (parent) parent.children.push(child);
      else out.push({ text: '', task: null, children: [child] });
    } else {
      out.push({ text: items[i].text, task: items[i].task, children: [] });
      i += 1;
    }
  }
  return i;
}

/** Markdown source → a flat list of block descriptors. */
export function parseBlocks(source) {
  const lines = String(source ?? '').replace(/\r\n?/g, '\n').split('\n');
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (!line.trim()) { i += 1; continue; }

    const fence = line.match(RE.fence);
    if (fence) {
      const body = [];
      i += 1;
      while (i < lines.length && !RE.fence.test(lines[i])) { body.push(lines[i]); i += 1; }
      i += 1;                                     // the closing fence, if it was ever typed
      blocks.push({ type: 'code', text: body.join('\n') });
      continue;
    }

    const heading = line.match(RE.heading);
    if (heading) {
      blocks.push({ type: 'heading', level: heading[1].length, text: heading[2] });
      i += 1;
      continue;
    }

    if (RE.rule.test(line)) { blocks.push({ type: 'rule' }); i += 1; continue; }

    if (RE.quote.test(line)) {
      const body = [];
      while (i < lines.length && RE.quote.test(lines[i])) {
        body.push(lines[i].match(RE.quote)[1]);
        i += 1;
      }
      blocks.push({ type: 'quote', blocks: parseBlocks(body.join('\n')) });
      continue;
    }

    if (RE.bullet.test(line) || RE.number.test(line)) {
      const ordered = !RE.bullet.test(line);
      const items = [];
      while (i < lines.length && (RE.bullet.test(lines[i]) || RE.number.test(lines[i]))) {
        const m = lines[i].match(RE.bullet) || lines[i].match(RE.number);
        const checkbox = m[2].match(RE.task);
        items.push({
          indent: m[1].replace(/\t/g, '  ').length,
          ordered: !RE.bullet.test(lines[i]),
          task: checkbox ? checkbox[1].toLowerCase() === 'x' : null,
          text: checkbox ? checkbox[2] : m[2],
        });
        i += 1;
        // A wrapped item: indented continuation that is not itself an item.
        while (i < lines.length && lines[i].trim() && /^\s{2,}/.test(lines[i])
               && !RE.bullet.test(lines[i]) && !RE.number.test(lines[i])) {
          items[items.length - 1].text += ` ${lines[i].trim()}`;
          i += 1;
        }
      }
      const tree = [];
      nestItems(items, 0, items[0].indent, tree);
      blocks.push({ type: 'list', ordered, items: tree });
      continue;
    }

    const para = [];
    while (i < lines.length && lines[i].trim() && !isBlockStart(lines[i])) {
      para.push(lines[i]);
      i += 1;
    }
    blocks.push({ type: 'para', lines: para });
  }

  return blocks;
}

function ListItems({ items, keyBase }) {
  return items.map((item, n) => (
    <li key={`${keyBase}-${n}`} data-task={item.task === null ? undefined : String(item.task)}>
      {item.task !== null && (
        <input type="checkbox" checked={item.task} readOnly tabIndex={-1} aria-hidden="true" />
      )}
      {inlineNodes(item.text, `${keyBase}-${n}`)}
      {item.children.map((child, c) => (
        child.ordered
          ? <ol key={c}><ListItems items={child.items} keyBase={`${keyBase}-${n}-${c}`} /></ol>
          : <ul key={c}><ListItems items={child.items} keyBase={`${keyBase}-${n}-${c}`} /></ul>
      ))}
    </li>
  ));
}

function Blocks({ blocks, keyBase = 'b' }) {
  return blocks.map((block, n) => {
    const key = `${keyBase}-${n}`;
    switch (block.type) {
      case 'heading': {
        // h1 in a task body is a section of the body, not a page title — the levels are shifted
        // so nothing in prose competes with the `.o-title` above it.
        const Tag = `h${Math.min(block.level + 2, 6)}`;
        return <Tag key={key}>{inlineNodes(block.text, key)}</Tag>;
      }
      case 'code':
        return <pre key={key}><code>{block.text}</code></pre>;
      case 'rule':
        return <hr key={key} />;
      case 'quote':
        return <blockquote key={key}><Blocks blocks={block.blocks} keyBase={key} /></blockquote>;
      case 'list':
        return block.ordered
          ? <ol key={key}><ListItems items={block.items} keyBase={key} /></ol>
          : <ul key={key}><ListItems items={block.items} keyBase={key} /></ul>;
      default:
        // A single newline inside a paragraph is a line break here, unlike in strict markdown.
        // People writing a bug report press Enter because they mean a new line, and reflowing
        // their steps into one paragraph is not a formatting opinion worth having.
        return (
          <p key={key}>
            {block.lines.map((line, l) => (
              <span key={l}>
                {l > 0 && <br />}
                {inlineNodes(line, `${key}-${l}`)}
              </span>
            ))}
          </p>
        );
    }
  });
}

/** Rendered markdown. `empty` is what shows when there is nothing to render. */
export default function Markdown({ text, empty = null, className = '' }) {
  const blocks = text && text.trim() ? parseBlocks(text) : [];
  if (!blocks.length) {
    return empty ? <p className="o-note" style={{ margin: 0 }}>{empty}</p> : null;
  }
  return (
    <div className={`o-prose ${className}`.trim()}>
      <Blocks blocks={blocks} />
    </div>
  );
}
